// Base
import { BaseApi } from 'api/base.api';

// Interface
import {
  IAssessmentPreparationApi,
  IAssessmentPreparationApiRequest,
  IAssessmentPreparationApiGetListAttrs,
  IAssessmentPreparationApiListResponse,
  IAssessmentPreparationApiUploadDocumentAttrs,
  IAssessmentPreparationApiDeleteDocumentAttrs,
  IAssessmentPreparationApiDownloadDocumentAttrs,
  IAssessmentPreparationApiDownloadDocumentResponse,
  IAssessmentPreparationApiNullResponse,
  IAssessmentPreparationApiSubmitAttrs,
  IAssessmentPreparationApiUploadDocumentResponse,
  IAssessmentPreparationDocumentDetailResponse,
  IAssessmentPreparationDocumentTemplateResponse,
  IAssessmentPreparationDocumentPICListResponse,
  IAssessmentPreparationDocumentDetailAssessmentResponse,
  IAssessmentPreparationDocumentConsentAssessmentResponse,
  IAssessmentPreparationDocumentImageIconsResponse,
  IAssessmentPreparationDocumentAnswerSheetResponse,
} from 'interfaces/back-office/assessment/preparation/preparation-api.interface';

// Constant
import { ApiConstantHc, ApiConstantHcBase } from 'constant';

// Utils
import { axios_renderHcUrl } from 'utils';
import {
  IAssessmentPreparationDocumentAnswerSheetAttrs,
  IAssessmentPreparationDocumentConsentAssessmentAttrs,
  IAssessmentPreparationDocumentDetailAssessmentAttrs,
  IAssessmentPreparationDocumentDetailAttrs,
  IAssessmentPreparationDocumentImageIconsAttrs,
  IAssessmentPreparationDocumentPICListAttrs,
  IAssessmentPreparationDocumentTemplateAttrs,
} from 'interfaces/back-office/assessment/preparation/preparation-view.interface';

export class AssessmentPreparationApi extends BaseApi<IAssessmentPreparationApi> implements IAssessmentPreparationApiRequest {
  readonly url = ApiConstantHc.ASSESSMENT_PREPARATION_URL;
  readonly urlAssessment = ApiConstantHc.ASSESSMENT_URL;
  readonly urlcCms = ApiConstantHc.CMS_DECISION_URL;

  private assessmentPreparationUrl = axios_renderHcUrl(this.url, ApiConstantHcBase.ASSESSMENT);
  private assessmentAssessmentUrl = axios_renderHcUrl(this.urlAssessment, ApiConstantHcBase.ASSESSMENT);
  private cmsDecisionUrl = axios_renderHcUrl(this.urlcCms, ApiConstantHcBase.ASSESSMENT);

  /**
   * @description Get assessment preparation list
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationApiGetListAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationApiListResponse>} Promise<IAssessmentPreparationApiListResponse>
   */
  async assessmentPreparation_getList(
    payload: IAssessmentPreparationApiGetListAttrs,
  ): Promise<IAssessmentPreparationApiListResponse> {
    return this.POST(`${this.assessmentPreparationUrl}/list/${payload.params.assessmentId}`, {}, { ...payload.requestConfig });
  }

  /**
   * @description Upload assessment preparation document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationApiUploadDocumentAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationApiUploadDocumentResponse>} Promise<IAssessmentPreparationApiUploadDocumentResponse>
   */
  async assessmentPreparation_uploadDocument(
    payload: IAssessmentPreparationApiUploadDocumentAttrs,
  ): Promise<IAssessmentPreparationApiUploadDocumentResponse> {
    if (payload.params.isDecision) {
      const formData = new FormData();
      formData.append('document', payload.body.document);
      formData.append('formSchemaId', payload.body.preparationId.toString());
      return this.POST(`${this.cmsDecisionUrl}/decision/uploadFormDocument/${payload.params.assessmentId}`, formData, {
        ...payload.requestConfig,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }

    if (payload.params.isAssessment) {
      const formData = new FormData();
      formData.append('document', payload.body.document);
      formData.append('id', payload.body.preparationId.toString());
      return this.POST(`${this.assessmentAssessmentUrl}/uploadDocument/${payload.params.assessmentId}`, formData, {
        ...payload.requestConfig,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }

    const formData = new FormData();
    formData.append('document', payload.body.document);
    formData.append('preparationId', payload.body.preparationId.toString());

    return this.POST(`${this.assessmentPreparationUrl}/uploadDocument/${payload.params.assessmentId}`, formData, {
      ...payload.requestConfig,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * @description Delete assessment preparation document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationApiDeleteDocumentAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationApiNullResponse>} Promise<IAssessmentPreparationApiNullResponse>
   */
  async assessmentPreparation_deleteDocument(
    payload: IAssessmentPreparationApiDeleteDocumentAttrs,
  ): Promise<IAssessmentPreparationApiNullResponse> {
    if (payload.params.isDecision) {
      return this.POST(
        `${this.cmsDecisionUrl}/decision/deleteFormDocument/${payload.params.assessmentId}`,
        { formSchemaId: payload.body.preparationId },
        { ...payload.requestConfig },
      );
    }
    if (payload.params.isAssessment) {
      return this.POST(
        `${this.assessmentAssessmentUrl}/deleteDocument/${payload.params.assessmentId}`,
        { id: payload.body.preparationId },
        { ...payload.requestConfig },
      );
    }
    return this.POST(`${this.assessmentPreparationUrl}/deleteDocument/${payload.params.assessmentId}`, payload.body, {
      ...payload.requestConfig,
    });
  }

  /**
   * @description Download assessment preparation document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationApiDownloadDocumentAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationApiDownloadDocumentResponse>} Promise<IAssessmentPreparationApiDownloadDocumentResponse>
   */
  async assessmentPreparation_downloadDocument(
    payload: IAssessmentPreparationApiDownloadDocumentAttrs,
  ): Promise<IAssessmentPreparationApiDownloadDocumentResponse> {
    if (payload.params.isAssessment) {
      return this.POST(
        `${this.assessmentAssessmentUrl}/download/${payload.params.blobId}`,
        {},
        { ...payload.requestConfig, responseType: 'blob' },
      );
    }
    return this.POST(
      `${this.assessmentPreparationUrl}/download/${payload.params.blobId}`,
      {},
      { ...payload.requestConfig, responseType: 'blob' },
    );
  }

  /**
   * @description Submit assessment preparation
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationApiSubmitAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationApiNullResponse>} Promise<IAssessmentPreparationApiNullResponse>
   */
  async assessmentPreparation_submit(
    payload: IAssessmentPreparationApiSubmitAttrs,
  ): Promise<IAssessmentPreparationApiNullResponse> {
    return this.POST(`${this.assessmentPreparationUrl}/submit/${payload.params.assessmentId}`, {}, { ...payload.requestConfig });
  }

  //new API
  /**
   * @description Get assessment preparation Document Detail
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationDocumentDetailAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationDocumentDetailResponse>} Promise<IAssessmentPreparationDocumentDetailResponse>
   */
  async assessmentPreparation_DocumentDetail(
    payload: IAssessmentPreparationDocumentDetailAttrs,
  ): Promise<IAssessmentPreparationDocumentDetailResponse> {
    return this.POST(
      `${this.assessmentPreparationUrl}/detailDocument/${payload.params.assessmentId}`,
      { ...payload.body },
      { ...payload.requestConfig },
    );
  }
  /**
   * @description Get assessment preparation Document Template
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationDocumentTemplateAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationDocumentTemplateResponse>} Promise<IAssessmentPreparationDocumentTemplateResponse>
   */
  async assessmentPreparation_DocumentTemplate(
    payload: IAssessmentPreparationDocumentTemplateAttrs,
  ): Promise<IAssessmentPreparationDocumentTemplateResponse> {
    return this.POST(
      `${this.assessmentPreparationUrl}/document/template/${payload.params.assessmentId}`,
      { ...payload.body },
      { ...payload.requestConfig },
    );
  }
  /**
   * @description Get assessment preparation Document PIC List
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationDocumentPICListAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationDocumentPICListResponse>} Promise<IAssessmentPreparationDocumentPICListResponse>
   */
  async assessmentPreparation_DocumentPICList(
    payload: IAssessmentPreparationDocumentPICListAttrs,
  ): Promise<IAssessmentPreparationDocumentPICListResponse> {
    return this.POST(
      `${this.assessmentPreparationUrl}/pic/list/${payload.params.assessmentId}`,
      { ...payload.body },
      { ...payload.requestConfig },
    );
  }
  /**
   * @description Get assessment preparation Document Detail Assessment
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationDocumentDetailAssessmentAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationDocumentDetailAssessmentResponse>} Promise<IAssessmentPreparationDocumentDetailAssessmentResponse>
   */
  async assessmentPreparation_DocumentDetailAssessment(
    payload: IAssessmentPreparationDocumentDetailAssessmentAttrs,
  ): Promise<IAssessmentPreparationDocumentDetailAssessmentResponse> {
    return this.POST(
      `${this.assessmentPreparationUrl}/detailAssessment/${payload.params.assessmentId}`,
      { ...payload.body },
      { ...payload.requestConfig },
    );
  }
  /**
   * @description Get assessment preparation Document Consent Assessment
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationDocumentConsentAssessmentAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationDocumentConsentAssessmentResponse>} Promise<IAssessmentPreparationDocumentConsentAssessmentResponse>
   */
  async assessmentPreparation_DocumentConsentAssessment(
    payload: IAssessmentPreparationDocumentConsentAssessmentAttrs,
  ): Promise<IAssessmentPreparationDocumentConsentAssessmentResponse> {
    return this.POST(
      `${this.assessmentPreparationUrl}/consentAssessment/${payload.params.assessmentId}`,
      { ...payload.body },
      { ...payload.requestConfig },
    );
  }
  /**
   * @description Get assessment preparation Document Consent Assessment
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationDocumentConsentAssessmentAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationDocumentConsentAssessmentResponse>} Promise<IAssessmentPreparationDocumentConsentAssessmentResponse>
   */
  async assessmentPreparation_DocumentImageIcons(
    payload: IAssessmentPreparationDocumentImageIconsAttrs,
  ): Promise<IAssessmentPreparationDocumentImageIconsResponse> {
    return this.POST(`${this.assessmentPreparationUrl}/GetImageIcons`, {}, { ...payload.requestConfig });
  }
  /**
   * @description Get assessment preparation Document Answer Sheet
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentPreparationDocumentAnswerSheetAttrs} payload
   *
   * @return {Promise<IAssessmentPreparationDocumentAnswerSheetResponse>} Promise<IAssessmentPreparationDocumentAnswerSheetResponse>
   */
  async assessmentPreparation_DocumentAnswerSheet(
    payload: IAssessmentPreparationDocumentAnswerSheetAttrs,
  ): Promise<IAssessmentPreparationDocumentAnswerSheetResponse> {
    return this.POST(
      `${this.assessmentPreparationUrl}/answerSheet/${payload.params.assessmentId}`,
      {
        preparationId: payload.body.preparationId,
      },
      { ...payload.requestConfig },
    );
  }
}
